import { useMemo, useState } from 'react';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import { useAttributeCatalog } from '@/services/db/attribute/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { isEmptyString } from '@/utils/string';
import type { AttributeNamePickerProps } from '../components/attribute-row/attribute-row';
import { attributeNameOptions } from './attribute-name-options';
import { AttributeNameSelect, type AttributeNameSearchState } from './attribute-name-select';

/**
 * The picker for an object attribute's name, shared by every surface that
 * filters or triggers on one.
 *
 * Shared because the names come from one place — the attributes seen on that
 * object type — whether the reader is filtering a list of objects or saying
 * what a campaign fires on. The profile-attribute picker beside it is the same
 * arrangement over a different catalogue.
 */

/** The picker's options, the term it is being searched by, and its state. */
export type ObjectAttributeNames = AttributeNameSearchState;

/**
 * The attribute-name options for one object type: whatever names the caller
 * already holds as a local seed, plus debounced server search scoped to that
 * type.
 *
 * How the pool is narrowed and what else the list carries is the shared rule in
 * `attributeNameOptions`.
 *
 * @param objectTypeId The object type whose attributes are offered.
 * @param selectedField The name the condition currently holds, or '' when fresh.
 * @param localNames Names the caller already has, offered before any search lands.
 */
export function useObjectAttributeNames(
  objectTypeId: string,
  selectedField: string,
  localNames: readonly string[] = [],
): ObjectAttributeNames {
  const workspaceId = requireWorkspaceId();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm] = useDebouncedValue(searchTerm, 300);
  const attributes = useAttributeCatalog(workspaceId, {
    objectTypeId,
    search: isEmptyString(debouncedTerm) ? undefined : debouncedTerm,
  });

  const searched = attributes.data?.pages;
  const options = useMemo(
    () => attributeNameOptions([localNames, ...(searched ?? [])], searchTerm, selectedField),
    [localNames, searched, searchTerm, selectedField],
  );

  return { options, searchTerm, setSearchTerm, isLoading: attributes.isFetching };
}

type ObjectAttributeNamePickerProps = AttributeNamePickerProps & {
  /** The options and search state, from `useObjectAttributeNames`. */
  names: ObjectAttributeNames;
};

/** The select an object-attribute row names its attribute with. */
export function ObjectAttributeNamePicker(props: ObjectAttributeNamePickerProps) {
  return (
    <AttributeNameSelect
      {...props}
      kindLabel="Object attribute"
      placeholder="Select an attribute"
      searchPlaceholder="Search attributes"
      testId="dryad-object-attribute-name"
    />
  );
}
